import { Env } from '@env';
import { Image } from 'expo-image';
import * as React from 'react';
import {
  ActivityIndicator,
  I18nManager,
  ScrollView,
  TextInput,
  View,
} from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Address } from '@/api/subscriptions/types';
import { generateSessionToken } from '@/core/auth/utils';
import { parseAddressComponents } from '@/core/utils';

import { Icon } from '../icon';
import { Building } from '../icons/building';
import { Location } from '../icons/location';
import { ListItem } from '../list-item';
import { reverseGeocode } from './reverse-geocode';

interface GoogleAutocompleteProps {
  onPress: (place: Address) => void;
  label: string;
  address?: Address;
  inputStyle?: any;
  onClear?: () => void;
  style?: any;
  listStyle?: any;
  onFocus?: () => void;
  onBlur?: () => void;
  leftIcon?: React.ReactNode;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

const darkPoweredByGoogleImg = require('../../../public/images/powered-by-google-dark.png');
const lightPoweredByGoogleImg = require('../../../public/images/powered-by-google-light.png');

export default function GoogleAutocomplete({
  onPress,
  label,
  address,
  inputStyle,
  onClear,
  style,
  listStyle,
  onFocus,
  onBlur,
  leftIcon,
  disabled,
  autoFocus,
  placeholder,
  ...inputProps
}: GoogleAutocompleteProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [state, setState] = React.useState<
    'default' | 'filled' | 'active' | 'error' | 'disabled'
  >('default');
  const [isFocused, setIsFocused] = React.useState(false);
  const [text, setText] = React.useState(address?.description || '');
  const [placeDetails, setPlaceDetails] = React.useState<any | null>(address);
  const containerRef = React.useRef<View>(null);
  const inputRef = React.useRef<TextInput>(null);
  const [currentLocationPermissionStatus, setCurrentLocationPermissionStatus] =
    React.useState('granted');
  const [loadingCurrentLocation, setLoadingCurrentLocation] =
    React.useState(false);
  const [placePredictions, setPlacePredictions] = React.useState<any[]>([]);
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] =
    React.useState(false);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const sessionTokenRef = React.useRef<string>(generateSessionToken());

  const getAutocompletePredictions = async (input: string) => {
    if (!input || input.length < 2) {
      setPlacePredictions([]);
      return;
    }

    setIsPlacePredictionsLoading(true);

    try {
      const response = await fetch(
        'https://places.googleapis.com/v1/places:autocomplete',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': Env.GOOGLE_APP_API_KEY,
          },
          body: JSON.stringify({
            input: input,
            sessionToken: sessionTokenRef.current,
          }),
        }
      );

      const data = await response.json();

      if (data.suggestions) {
        setPlacePredictions(
          data.suggestions.map(({ placePrediction }: any) => {
            return {
              description: placePrediction.text.text,
              placeId: placePrediction.placeId,
            };
          })
        );
      } else {
        setPlacePredictions([]);
      }
    } catch (error) {
      console.error('Error fetching place predictions:', error);
      setPlacePredictions([]);
    } finally {
      setIsPlacePredictionsLoading(false);
    }
  };

  const refreshSessionToken = () => {
    sessionTokenRef.current = generateSessionToken();
  };

  const getPlaceDetails = async (placeId: string) => {
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}?sessionToken=${sessionTokenRef.current}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': Env.GOOGLE_APP_API_KEY,
            'X-Goog-FieldMask': 'address_components,formatted_address,location',
          },
        }
      );

      const data = await response.json();

      const city = data.addressComponents.find((c: any) =>
        c.types.includes('locality')
      )?.longText;
      const s = data.addressComponents.find((c: any) =>
        c.types.includes('administrative_area_level_1')
      )?.longText;
      const zipcode = data.addressComponents.find((c: any) =>
        c.types.includes('postal_code')
      )?.longText;

      return {
        addressComponents: data.addressComponents,
        description: data.formattedAddress,
        placeId: placeId,
        latitude: data.location.latitude,
        longitude: data.location.longitude,
        city,
        state: s,
        zipcode,
        ...parseAddressComponents(data.addressComponents),
      };
    } catch (error) {
      console.error('Error fetching place details:', error);
      return null;
    }
  };

  const getCurrentLocation = async () => {
    try {
      const getPosition = () =>
        new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve(position),
            (error) => reject(error),
            { enableHighAccuracy: false, timeout: 5000, maximumAge: 120000 }
          );
        });

      const position = await getPosition();

      return {
        description: 'Current Location',
        placeId: 'current-location',
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    } catch (e: any) {
      if (e?.code === 1) {
        setCurrentLocationPermissionStatus('denied');
      }
      console.log('e', e);
    }
  };

  const places = React.useMemo(() => {
    return [
      {
        description: 'Current Location',
        placeId: 'current-location',
        disabled: currentLocationPermissionStatus !== 'granted',
      },
      ...placePredictions,
    ];
  }, [placePredictions, currentLocationPermissionStatus]);

  React.useEffect(() => {
    if (!autoFocus) return;

    inputRef.current?.focus();
  }, [autoFocus]);

  React.useEffect(() => {
    setText(address?.description || '');
  }, [address]);

  React.useEffect(() => {
    if (isFocused) {
      setState('active');
      return;
    }

    if (disabled) {
      setState('disabled');
      return;
    }

    if (!isFocused && text.length > 0) {
      setState('filled');
      return;
    }

    setState('default');
  }, [text, isFocused]);

  // Add this debounced effect to fetch predictions when text changes
  React.useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new timer
    debounceTimerRef.current = setTimeout(() => {
      getAutocompletePredictions(text);
    }, 800);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [text]);

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
  }, [placeDetails]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleBlur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef, handleBlur]);

  return (
    <View ref={containerRef} style={[style]}>
      <View style={[styles.textContainer, styles[`${state}`]]}>
        {leftIcon}

        <TextInput
          ref={inputRef}
          label={label}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.text.secondary}
          value={text}
          onFocus={() => {
            if (disabled) return;

            onFocus?.();
            setIsFocused(true);
          }}
          onBlur={() => {
            onBlur?.();
          }}
          onChangeText={(t: string) => {
            setText(t);
            setPlaceDetails(null);

            if (onClear && t.length === 0) {
              onClear();
            }
          }}
          style={[
            {
              writingDirection: I18nManager.isRTL ? 'rtl' : 'ltr',
              outline: 'none',
            },
            styles.input,
            styles[`${state}Text`],
          ]}
          inputStyle={[styles.input, inputStyle]}
          editable={disabled ? false : true}
          {...inputProps}
        />
      </View>

      {isFocused ? (
        <View
          style={{
            width: '100%',
            flexGrow: 1,
            backgroundColor: theme.colors.background_2,
            padding: 16,
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
            borderColor: theme.colors.dropdown.borderColor,
            borderWidth: 1,
            position: 'absolute',
            top: 55,
            zIndex: 100,
            ...listStyle,
          }}
        >
          <ScrollView
            contentContainerStyle={{
              paddingBottom: 16,
              gap: 8,
            }}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
          >
            {isPlacePredictionsLoading ? (
              <ActivityIndicator
                size="small"
                color={theme.colors.text.secondary}
              />
            ) : (
              places.map((item: any) => (
                <ListItem
                  key={item.placeId}
                  text={
                    item.description || item.structured_formatting?.main_text
                  }
                  onPress={async () => {
                    if (item.disabled) return;

                    if (item.description === 'Current Location') {
                      setLoadingCurrentLocation(true);

                      try {
                        const currentLocation = await getCurrentLocation();

                        if (currentLocation) {
                          const { latitude: lat, longitude: lng } =
                            currentLocation;
                          const geocoded = await reverseGeocode(lat, lng);

                          onPress(
                            geocoded ?? {
                              addressComponents: [],
                              description: 'Current Location',
                              placeId: 'current-location',
                              latitude: lat,
                              longitude: lng,
                              city: '',
                              state: '',
                              zipcode: '',
                            }
                          );
                          setIsFocused(false);
                        }
                      } finally {
                        setLoadingCurrentLocation(false);
                      }

                      return;
                    }

                    setPlaceDetails(item);
                    setText(
                      item.description || item.structured_formatting?.main_text
                    );

                    const pd = await getPlaceDetails(item.placeId);

                    if (pd) {
                      refreshSessionToken();
                      onPress(pd);
                      setIsFocused(false);
                    }
                  }}
                  icon={
                    item.description === 'Current Location' &&
                    loadingCurrentLocation ? (
                      <ActivityIndicator
                        size={32}
                        color={theme.colors.text.secondary}
                      />
                    ) : (
                      <Icon
                        type="dressed"
                        icon={
                          item.description === 'Current Location' ? (
                            <Location />
                          ) : (
                            <Building />
                          )
                        }
                        fillColor={theme.colors.icon.color}
                        width={25}
                        height={25}
                      />
                    )
                  }
                  withBorder={false}
                  style={{ borderBottomWidth: 0 }}
                  textStyle={{
                    color: item.disabled
                      ? theme.colors.text.secondary50
                      : theme.colors.text.secondary,
                  }}
                  disabled={item.disabled}
                  caption={item.disabled ? 'Location permission denied' : ''}
                  captionStyle={
                    item.disabled
                      ? { color: theme.colors.text.secondary50 }
                      : {}
                  }
                />
              ))
            )}

            <View style={{ alignItems: 'flex-end' }}>
              <Image
                source={
                  UnistylesRuntime.themeName === 'dark'
                    ? darkPoweredByGoogleImg
                    : lightPoweredByGoogleImg
                }
                style={{
                  width: 100,
                  aspectRatio: 8,
                  contentFit: 'cover',
                }}
              />
            </View>
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    flexShrink: 1,
    gap: 8,
  },
  default: {
    borderColor: theme.colors.input.default.border,
  },
  defaultText: {
    color: theme.colors.input.default.text,
  },
  defaultHelperText: {
    color: theme.colors.input.default.helperText,
  },
  filled: {
    borderColor: theme.colors.input.filled.border,
  },
  filledText: {
    color: theme.colors.input.filled.text,
  },
  filledHelperText: {
    color: theme.colors.input.filled.helperText,
  },
  active: {
    borderColor: theme.colors.input.active.border,
  },
  activeText: {
    color: theme.colors.input.active.text,
  },
  activeHelperText: {
    color: theme.colors.input.active.helperText,
  },
  error: {
    borderColor: theme.colors.input.error.border,
  },
  errorText: {
    color: theme.colors.input.error.text,
  },
  errorHelperText: {
    color: theme.colors.input.error.helperText,
  },
  disabled: {
    backgroundColor: theme.colors.input.disabled.fill,
    borderColor: theme.colors.input.disabled.border,
  },
  disabledText: {
    color: theme.colors.input.disabled.text,
  },
  disabledHelperText: {
    color: theme.colors.input.disabled.helperText,
  },
  textContainer: {
    width: '100%',
    height: 56,
    borderWidth: 1,
    gap: 16,
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  label: {
    paddingLeft: 4,
    paddingRight: 4,
    left: 12,
    backgroundColor: theme.colors.background_2,
  },
  input: {
    flex: 1,
    height: '100%',
    outlineWidth: 0,
    fontSize: 16,
    lineHeight: 22.4,
  },
  eye: {
    paddingRight: 16,
  },
}));
